import "./index.scss";

import { Box, MenuItem, SelectChangeEvent, Stack } from "@mui/material";
import { ReactComponent as IconMember } from "assets/images/side-menu/members-on.svg";
import { ReactComponent as IconMemberAdd } from "assets/images/square-buttons/member-add.svg";
import ConfirmationBox from "components/ConfirmationBox";
import Dropdown from "components/Dropdown";
import Pagination, { PAGE_OPTIONS } from "components/Pagination";
import ScreenLoader from "components/ScreenLoader";
import SearchBar from "components/SearchBar";
import Tabs from "components/Tabs";
import { DEFAULT_MEMBER_STATUS } from "constants/index";
import CustomSelect from "containers/Members/Select";
import SortButton from "containers/Members/SortButton";
import { useQueryParams } from "hooks/useQueryParams";
import { TCAMembersListEntry } from "models/member";
import { TPagination } from "modules/api/type";
import { appActions } from "modules/app";
import {
  deleteTCAMemberAppointments,
  getTCAMembers,
  updateMemberStatus,
} from "modules/member/memberServices";
import { memo, useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useAppDispatch } from "redux/hooks";

import MemberFormModal from "../../Members/MemberFormModal";
import FollowUpTCASteps from "../FollowUpTCASteps";
import AddMemberToCoachApproachModal from "./AddMemberToCoachApproachModal";
import ExecuteCoachApproachModal from "./ExecuteCoachApproachModal";
import { TableItem } from "./TableComponents";

const MembersJoinedComponent = () => {
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<TPagination>({
    current_page: 0,
    data_from_offset: 0,
    data_to_offset: 0,
    per_page: PAGE_OPTIONS[0],
    total_pages: 0,
    total_records: 0,
  });

  const [tabValue, setTabValue] = useState(0);
  const [tableEntries, setTableEntries] = useState<TCAMembersListEntry[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const [searchBy, setSearchBy] = useState<string>("LAST_NAME");
  const [action, setAction] = useState("");
  const [memberToExecuteTCASteps, setMemberToExecuteTCASteps] = useState();
  const [memberToExecuteTCAFollowupSteps, setMemberToExecuteTCAFollowupSteps] = useState();
  const [showExecuteApproachModal, setShowExecuteApproachModal] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showTCAFollowupModal, setShowTCAFollowupModal] = useState(false);
  const [memberIdToProcess, setMemberIdToProcess] = useState<number>();
  const [showMemberFormModal, setShowMemberFormModal] = useState<boolean>(false);
  const [confirmationBoxShown, setConfirmationBoxShown] = useState(false);
  const [confirmationBoxMainTitle, setConfirmationBoxMainTitle] = useState("");
  const [confirmationBoxTitle, setConfirmationBoxTitle] = useState("");
  const [confirmationBoxMessage, setConfirmationBoxMessage] = useState(<></>);
  const [confirmationBoxType, setConfirmationBoxType] = useState<"confirmation" | "alert" | "info">(
    "confirmation"
  );
  const [confirmationBoxOnClose, setConfirmationBoxOnClose] = useState(() => void 0);
  const [confirmationBoxIsLoading, setConfirmationBoxIsLoading] = useState(false);
  const [params, setParams, defaultParams] = useQueryParams({
    limit: 10,
    my_members_only: true,
    page: 1,
    search: "",
    sort_by: "LAST_NAME",
    sort_order: "ASC",
    status: DEFAULT_MEMBER_STATUS,
  });
  const dispatch = useAppDispatch();

  dispatch(
    appActions.updatePageTitle({
      title: `TCA Members`,
    })
  );

  useEffect(() => {
    fetchData(params);
  }, [params]);

  useEffect(() => {
    setParams("page", 1);
    setParams("search", searchValue || "");
  }, [searchValue]);

  useEffect(() => {
    setParams("page", 1);
    setParams("search_by", searchBy);
  }, [searchBy]);

  useEffect(() => {
    setParams("my_members_only", tabValue === 1 ? false : true);
  }, [tabValue]);

  const fetchData = useCallback(
    async (parameters) => {
      if (loading) return;
      setLoading(true);
      let result = await getTCAMembers(parameters);
      setLoading(false);
      if (result.success) {
        setTableEntries(result.data);
        setPagination(result.pagination);
      }
    },
    [setTableEntries, setPagination, loading, setLoading]
  );

  //#region functions
  const showFollowupWindow = (item) => {
    setMemberToExecuteTCAFollowupSteps(item);
    setShowTCAFollowupModal(true);
  };
  //#endregion functions

  //#region event handlers
  const handlePaginationOptionChange = (event: SelectChangeEvent<any>) => {
    let temp = { ...params, limit: event.target.value, page: 1 };
    setParams("limit", event.target.value);
    fetchData(temp);
  };

  const handlePageChange = (page: number) => {
    let temp = { ...params, page };
    setParams("page", page);
    fetchData(temp);
  };

  const handleSortChange = (sort_by: string, sort_order: string) => {
    let temp = { ...params, page: 1, sort_by, sort_order };
    //console.log("tempParams: ", temp);
    setParams("page", 1);
    setParams("sort_by", sort_by);
    setParams("sort_order", sort_order);
    fetchData(temp);
  };

  const onSelectAction = (e) => {
    setAction(e.target.value);
    if (e.target.value === "execute_approach") {
      setShowExecuteApproachModal(true);
    } else {
      setMemberToExecuteTCASteps(null);
      setShowAddMemberModal(true);
    }
  };

  const onCloseActionModal = (isRefreshNeeded = false) => {
    setShowExecuteApproachModal(false);
    setShowAddMemberModal(false);
    setAction("");
    if (typeof isRefreshNeeded == "boolean" && isRefreshNeeded) {
      setLoading(true);
      window.location.reload();
    }
  };

  const onCloseExecuteCoachApproachModal = (item) => {
    setShowExecuteApproachModal(false);
    if (item) {
      setMemberToExecuteTCASteps(item);
      setShowAddMemberModal(true);
    }
    setAction("");
  };

  const onCloseTCAFollowupModal = (isRefreshNeeded = false) => {
    setShowTCAFollowupModal(false);
    if (typeof isRefreshNeeded == "boolean" && isRefreshNeeded) {
      setLoading(true);
      window.location.reload();
    }
  };

  const handleStatusChange = (values) => {
    setParams("status", values.join(","));
  };

  const onUpdateStatus = (id, value) => {
    return new Promise((resolve) => {
      updateMemberStatus({ member_id: id, member_status: value })
        .then((result) => {
          if (result.success) {
            //show success message
            toast("Member state updated successfully", { type: "success" });
          } else {
            toast("Member state update failed", { type: "error" });
          }
        })
        .catch((e) => {
          toast("Member state update failed", { type: "error" });
        })
        .finally(() => resolve(true));
    });
  };

  const handleFollowupAppointment = (item) => {
    // if overdue, go through questions
    if (item.last_appointment_75_days_ago_flag) {
      setConfirmationBoxMainTitle("Last Appointment Warning");
      setConfirmationBoxTitle(
        "It has been 75 or more days since this member missed their last scheduled appointment."
      );
      setConfirmationBoxMessage(
        <Box mt={2} mx={2}>
          <ul>
            <li>
              Press YES to RESET all of this member's COACH APPROACH information (Recommended).
              <br />
              <br />
            </li>
            <li>Press NO to Ignore and continue.</li>
          </ul>
        </Box>
      );
      setConfirmationBoxOnClose((_prev, _props) => {
        return (v) => {
          if (typeof v !== "boolean") {
            setConfirmationBoxShown(false);
            return;
          }
          if (v) {
            //Reconfirm before reset
            setConfirmationBoxMainTitle("Reset Member Verify");
            setConfirmationBoxTitle(
              "Are you sure you want to Reset this COACH APPROACH member (member would need to restart from the Initial Appointment again)."
            );
            setConfirmationBoxMessage(<></>);
            setConfirmationBoxOnClose((_prev, _props) => {
              return (v) => {
                if (typeof v !== "boolean" || v === false) {
                  setConfirmationBoxShown(false);
                  return;
                }
                if (v) {
                  //Call reset function and then initiate user to initial appointment
                  setConfirmationBoxMainTitle("Resetting Member");
                  setConfirmationBoxIsLoading(true);
                  deleteTCAMemberAppointments({ full_delete: true, member_id: item.id })
                    .then((_res) => {
                      setConfirmationBoxType("info");
                      setConfirmationBoxMainTitle("Member Reset Successful!");
                      setConfirmationBoxTitle(
                        `Member ${item.first_name} ${item.last_name} has been Reset. You will need to set up an Initial Appointment again to begin.`
                      );
                      setConfirmationBoxMessage(<></>);
                      setConfirmationBoxOnClose((_prev, _props) => {
                        return (v) => {
                          setConfirmationBoxShown(false);
                          setLoading(true);
                          window.location.reload();
                        };
                      });
                      setConfirmationBoxIsLoading(false);
                    })
                    .catch((e) => {
                      console.log(`error:\n`, e);
                    });
                }
              };
            });
          } else {
            //proceed with current user to next follow up appointment
            showFollowupWindow(item);
            setConfirmationBoxShown(false);
          }
        };
      });
      setConfirmationBoxShown(true);
    } else {
      showFollowupWindow(item);
    }
  };

  const handleCloseMemberFormModal = (isRefreshNeeded = false) => {
    setShowMemberFormModal(false);
    if (typeof isRefreshNeeded == "boolean" && isRefreshNeeded) {
      setLoading(true);
      window.location.reload();
    }
  };

  const handleSearch = (searchValue) => {
    let value = searchValue;
    if (searchBy === "FITLINXX_ID" && value !== "") {
      value = parseInt(value);
    }
    setSearchValue(value);
  };
  //#endregion event handlers

  return (
    <>
      <div className={"members-main"}>
        <Tabs
          value={tabValue}
          onChange={setTabValue}
          tabs={[{ label: "My Members" }, { label: "All Members" }]}
          rightComp={
            <Stack direction="row" gap={"10px"} className="members-joined-right-component">
              <CustomSelect defaultOptions={[DEFAULT_MEMBER_STATUS]} handler={handleStatusChange} />
              <Stack
                direction={"row"}
                alignItems="center"
                className="search-with-dropdown-container"
              >
                <SearchBar
                  className="search-container-with-dropdown"
                  placeholder="Search"
                  value={searchValue}
                  onSearch={handleSearch}
                  isAsync={true}
                />
                <Stack height={"90%"} style={{ background: "#d6eeec", width: 1 }} />
                <Dropdown
                  name="search_field"
                  label="All field"
                  className="base-dropdown"
                  value={searchBy}
                  defaultValue={""}
                  onChange={(e) => setSearchBy(`${e.target.value}`)}
                  showLabel={false}
                >
                  <MenuItem value="LAST_NAME">Last Name</MenuItem>
                  <MenuItem value="FIRST_NAME">First Name</MenuItem>
                  <MenuItem value="FITLINXX_ID">FitLinxx ID</MenuItem>
                </Dropdown>
              </Stack>
              <Dropdown
                name="search_field"
                label="Actions"
                value={action}
                defaultValue={action}
                onChange={onSelectAction}
                className="base-dropdown"
                // className="select"
                style={{
                  width: 120,
                }}
              >
                {/* <MenuItem value="actions">Actions</MenuItem> */}
                <MenuItem value="new_member">
                  <Stack direction={`row`} gap={"10px"} mt="10px">
                    <IconMemberAdd width={24} height={24} />
                    Add a New Member to The Coach Approach&reg;
                  </Stack>
                </MenuItem>
                <MenuItem value="execute_approach">
                  <Stack direction={`row`} gap={"10px"} mb="10px">
                    <IconMember width={24} height={24} />
                    Execute The Coach Approach&reg; Steps
                  </Stack>
                </MenuItem>
              </Dropdown>
            </Stack>
          }
        />
        <div className={"sorting-buttons"}>
          <Box className="image"></Box>
          <SortButton
            label={`Last Name`}
            value={`LAST_NAME`}
            style={{ flex: 3 }}
            sortingCallback={handleSortChange}
            pageParams={{ sort_by: params.sort_by, sort_order: params.sort_order }}
          />
          <SortButton
            label={`First Name`}
            value={`FIRST_NAME`}
            style={{ flex: 3 }}
            sortingCallback={handleSortChange}
            pageParams={{ sort_by: params.sort_by, sort_order: params.sort_order }}
          />
          <SortButton
            label={`Next Appointment`}
            value={`NEXT_APPOINTMENT_NUMBER`}
            style={{ flex: 4 }}
            sortingCallback={handleSortChange}
            pageParams={{ sort_by: params.sort_by, sort_order: params.sort_order }}
          />
          <SortButton
            label={`Next Appointment Date`}
            value={`NEXT_APPOINTMENT_DATE`}
            style={{ flex: 4 }}
            sortingCallback={handleSortChange}
            pageParams={{ sort_by: params.sort_by, sort_order: params.sort_order }}
          />
          <SortButton
            label={`FitLinxx ID`}
            value={`FITLINXX_ID`}
            style={{ flex: 3 }}
            sortingCallback={handleSortChange}
            pageParams={{ sort_by: params.sort_by, sort_order: params.sort_order }}
          />
          <SortButton
            label={`Member Status`}
            value={`STATUS`}
            style={{ flex: 5 }}
            sortingCallback={handleSortChange}
            pageParams={{ sort_by: params.sort_by, sort_order: params.sort_order }}
          />
          <Box display="flex" alignItems="center" justifyContent={"end"} width={240}>
            <label className="label">Actions</label>
          </Box>
        </div>
        {!loading &&
          tableEntries.map((e, index) => (
            <TableItem
              data={e}
              key={`tableitem${index}`}
              followupCallback={handleFollowupAppointment}
              onUpdateStatus={onUpdateStatus}
              onClickEdit={(id) => {
                console.log("onClickEdit", id);
                setMemberIdToProcess(id);
                setShowMemberFormModal(true);
              }}
            />
          ))}
        {loading && (
          <Box mt="30px">
            <ScreenLoader isScreenLoader={true} />
          </Box>
        )}
        <Pagination
          {...pagination}
          onPageChange={handlePageChange}
          pageDropdownProps={{
            defaultValue: pagination.per_page,
            onChange: handlePaginationOptionChange,
            value: pagination.per_page,
          }}
        />
        <AddMemberToCoachApproachModal
          open={showAddMemberModal}
          onClose={onCloseActionModal}
          existingMember={memberToExecuteTCASteps}
        />
        <ExecuteCoachApproachModal
          open={showExecuteApproachModal}
          onClose={onCloseExecuteCoachApproachModal}
        />
        <FollowUpTCASteps
          open={showTCAFollowupModal}
          onClose={onCloseTCAFollowupModal}
          member={memberToExecuteTCAFollowupSteps}
        />
      </div>
      <MemberFormModal
        open={showMemberFormModal}
        onClose={handleCloseMemberFormModal}
        memberId={memberIdToProcess}
      />
      <ConfirmationBox
        open={confirmationBoxShown}
        onClose={confirmationBoxOnClose}
        mainTitle={confirmationBoxMainTitle}
        title={confirmationBoxTitle}
        message={confirmationBoxMessage}
        type={confirmationBoxType}
        isLoading={confirmationBoxIsLoading}
      />
    </>
  );
};

const MembersJoined = memo(MembersJoinedComponent);
export default MembersJoined;
